/**
 * Email Verification Page
 * Handles the email link sign-in completion
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AnimatedBackground } from '@/components/ui/animated-background';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { pages } from '@/Environment';
import { motion } from 'framer-motion';
import EmailConfirmModal from '@/components/EmailConfirmModal';

export default function EmailVerification() {
  const { completeSignIn } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'needs-email'
  const [error, setError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasPendingAnalysis, setHasPendingAnalysis] = useState(false);

  // Check if user has pending analysis data in localStorage
  const checkPendingAnalysis = () => {
    const analysisData = localStorage.getItem('analysisData');
    return !!analysisData;
  };

  // Redirect to appropriate page based on pending analysis
  const redirectAfterSuccess = () => {
    const hasPending = checkPendingAnalysis();
    setHasPendingAnalysis(hasPending);

    setTimeout(() => {
      if (hasPending) {
        // User has pending analysis — redirect to results with welcome flag
        navigate(pages.REVIEW, { state: { fromVerification: true } });
      } else {
        // No analysis data (different device, cleared storage) — go to upload
        navigate(pages.UPLOAD);
      }
    }, 2000);
  };

  useEffect(() => {
    const verify = async () => {
      const url = window.location.href;
      const result = await completeSignIn(url);

      if (result.success) {
        setStatus('success');
        redirectAfterSuccess();
      } else if (result.needsEmail) {
        // Show email confirmation modal
        setStatus('needs-email');
        setShowEmailModal(true);
      } else {
        setStatus('error');
        const errorMsg = result.error || 'Failed to verify email link';

        // Provide helpful error messages
        if (errorMsg.includes('network-request-failed')) {
          setError('Network connection failed. Please check your internet and try the link again.');
        } else if (errorMsg.includes('expired')) {
          setError('This link has expired. Please request a new sign-in link.');
        } else if (errorMsg.includes('invalid')) {
          setError('Invalid sign-in link. Please request a new one.');
        } else {
          setError(errorMsg);
        }
      }
    };

    verify();
  }, [completeSignIn, navigate]);

  const handleEmailConfirm = async (email) => {
    setShowEmailModal(false);
    setStatus('verifying');

    const url = window.location.href;
    const result = await completeSignIn(url, email);

    if (result.success) {
      setStatus('success');
      redirectAfterSuccess();
    } else {
      setStatus('error');
      setError(result.error || 'Failed to verify email. Please check that you entered the correct email address.');
    }
  };

  const handleEmailCancel = () => {
    setShowEmailModal(false);
    setStatus('error');
    setError('Email confirmation cancelled. Please try signing in again.');
  };

  return (
    <>
      <EmailConfirmModal
        isOpen={showEmailModal}
        onConfirm={handleEmailConfirm}
        onCancel={handleEmailCancel}
      />
      <AnimatedBackground className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Email Verification</CardTitle>
            </CardHeader>
            <CardContent className="text-center py-8">
              {status === 'verifying' && (
                <div>
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary-500" />
                  <p className="text-neutral-600 dark:text-neutral-400">Verifying your email...</p>
                </div>
              )}

              {status === 'needs-email' && !showEmailModal && (
                <div>
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary-500" />
                  <p className="text-neutral-600 dark:text-neutral-400">Please confirm your email address...</p>
                </div>
              )}

            {status === 'success' && (
              <div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="mx-auto w-16 h-16 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mb-4"
                >
                  <CheckCircle className="h-10 w-10 text-success-700" />
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">Success!</h3>
                <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                  Your email has been verified. You now have 10 free analyses per month!
                </p>
                <p className="text-sm text-neutral-500">
                  {hasPendingAnalysis
                    ? "Taking you to your full report..."
                    : "Redirecting..."}
                </p>
              </div>
            )}

            {status === 'error' && (
              <div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="mx-auto w-16 h-16 bg-danger-100 dark:bg-danger-900/30 rounded-full flex items-center justify-center mb-4"
                >
                  <XCircle className="h-10 w-10 text-danger-700" />
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">Verification Failed</h3>
                <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => navigate(pages.UPLOAD)} variant="outline">
                    Go to Upload Page
                  </Button>
                  <Button 
                    onClick={() => window.location.href = window.location.href}
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AnimatedBackground>
    </>
  );
}
